import { z } from 'zod';
import { request, useQuery } from '@applyboard/ui-utils';

const RawFxRatesSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal('fx_rates'),
    attributes: z.object({
      source: z.string(),
      quotes: z.array(
        z.object({
          currency: z.string(),
          rate: z.string(),
        }),
      ),
    }),
  }),
});

export type RawFxRates = z.infer<typeof RawFxRatesSchema>;

export type FxRates = {
  sourceCurrency: string;
  rates: Record<string, number>;
};

export function useGetLatestFxRates(
  options: { enabled?: boolean; retryCount: number; retryOnMount?: boolean } = { retryCount: 2 },
) {
  const { isLoading, isFetching, error, data } = useQuery(
    ['useGetLatestFxRates', { pkg: 'ab-vas-gic' }],
    async () => {
      const response = await request<RawFxRates>('/api/payments/v1/fx_rates/latest', {
        schema: RawFxRatesSchema,
        allowAnonymous: true,
      });

      return response;
    },
    {
      enabled: options.enabled,
      refetchOnMount: false,
      retryOnMount: options.retryOnMount,
      retry: options.retryCount,
    },
  );

  const fxRates = data
    ? {
        sourceCurrency: data.data.attributes.source,
        rates: data.data.attributes.quotes.reduce(
          (acc, quote) => {
            acc[quote.currency] = parseFloat(quote.rate);
            return acc;
          },
          {} as Record<string, number>,
        ),
      }
    : undefined;

  return {
    rawFxRates: data,
    fxRates,
    fxRatesError: error,
    isLoadingFxRates: isLoading,
    isFetchingFxRates: isFetching,
  };
}
